import { createContext, useContext, useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { checkSession } from "../../api/login/UserLogin";
import { getMemberSearchData, getAllCommittees, getAllEvents, getEventTypes } from "../../api/admin/AdminDashboardAPI";
import { getSemesters } from "../../api/admin/AdminSettingsAPI";
import { LocalDate } from "@js-joda/core";
import UnauthorizedPage from "../UnauthorizedPage";
import Page from "../../components/Page";


const AdminContext = createContext();

export const AdminProvider = ({ children }) => {
    // need to get members, events, and committees from backend
    const [members, setMembers] = useState([]);
    const [events, setEvents] = useState([]);
    const [semesters, setSemesters] = useState([]);
    const [committees, setCommittees] = useState([]);
    const [eventTypes, setEventTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [apiSuccess, setApiSuccess] = useState(true);
    const [roles, setRoles] = useState([]);

    const location = useLocation();
    const navigate = useNavigate();

    const intervalId = useRef(null);

    useEffect(() => {( async () => {
        setLoading(true); // Start loading
        const allRoles = await checkSession(location.pathname, navigate);
        setRoles(allRoles);
        intervalId.current = setInterval(() => {
            checkSession(location.pathname, navigate);
        }, 15 * 60 * 1000); // every 15 min, check session

        // Cleanup interval on unmount
        return () => clearInterval(intervalId.current);
    })();
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const [allMembers, allEvents, allCommittees, allTypes, allSemesters] = await Promise.all([
                    getMemberSearchData(),
                    getAllEvents(),
                    getAllCommittees(),
                    getEventTypes(),
                    getSemesters()
                ]);

                setMembers(allMembers)
                setEvents(allEvents);
                setCommittees(allCommittees);
                setEventTypes(allTypes);
                setSemesters(allSemesters);
            } catch (error) {
                console.error("Error fetching member data:", error);
                setApiSuccess(false);
            } finally {
                setLoading(false); // Stop loading once data is fetched
            }
        })();
    }, []);

    const addEvent = (newEvent) => {
        setEvents((prevEvents) => [...(prevEvents ?? []), newEvent]);
    };

    const addCommittee = (newCommittee) => {
        setCommittees((prevCommittees) => [...(prevCommittees ?? []), newCommittee]);
    };

    const updateEvents = (updatedEvent) => {
        setEvents(prevEvents =>
            prevEvents.map(ev => ev.id === updatedEvent.id ? updatedEvent : ev
            ));
    }

    const updateCommittees = (updatedCommittee) => {
        setCommittees(prevCommittees =>
            prevCommittees.map(ev => ev.id === updatedCommittee.id ? updatedCommittee : ev
            ));
    }

    const removeEvent = (rEvent) => {
        const id = rEvent.id;
        setEvents((prevEvents) => (prevEvents ?? []).filter(event => event.id !== id));
    }

    const removeCommittee = (rCommittee) => {
        const id = rCommittee.id;
        setCommittees((prevCommittees) => (prevCommittees ?? []).filter(committee => committee.id !== id));
    }

    const updateMembers = (updatedMember) => {
        setMembers(prevMembers =>
            prevMembers.map(ev => ev.id === updatedMember.id ? updatedMember : ev
            ));
        window.location.reload();
    }

    const getCurrentSemester = () => {
        const today = LocalDate.now();
        return semesters.find((semester) => {
            const startDate = LocalDate.parse(semester.startDate);
            const endDate = LocalDate.parse(semester.endDate);
            return (today.equals(startDate) || today.isAfter(startDate))
                && (today.equals(endDate) || today.isBefore(endDate));
        });
    }

    
    const correctRolesForPage = ["ADMIN", "CHEAD", "LOGHEAD"].some(r => roles.includes(r));

    if (loading) {
        return (
            <Page>
                <div>
                    Loading....
                </div>
            </Page>
            
        );
    }

    if (!correctRolesForPage && !loading) {
        return (
            <UnauthorizedPage />
        )
    }

    return (
        <AdminContext.Provider value={{ roles, members, events, committees, semesters, eventTypes, loading, apiSuccess, addEvent, updateEvents, setEvents, removeEvent, updateMembers, updateCommittees, addCommittee, removeCommittee, setCommittees }}>
            {children}
        </AdminContext.Provider>
    );
};


export const AdminLayout = () => {
    return (
        <AdminProvider>
            <Outlet />
        </AdminProvider>
    );
}

export const useAdmin = () => useContext(AdminContext);